// Pagina publica: el usuario atiende un ticket desde el enlace del correo, sin
// iniciar sesion (el token firmado del enlace identifica ticket y usuario).
// Si el ticket espera su confirmacion: "Quedo solucionado" (con calificacion)
// o "No esta solucionado" (explicando por que). Si no, un mensaje simple.
import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Star } from 'lucide-react';
import { config } from '../config';

const http = axios.create({ baseURL: config.api.baseURL, timeout: config.api.timeout });

const formatear = (iso) =>
  new Date(iso).toLocaleString('es-CL', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });

const CRITERIOS = [
  { id: 'calificacion', label: 'Calificación general', requerida: true },
  { id: 'calificacionRapidez', label: 'Rapidez' },
  { id: 'calificacionAtencion', label: 'Atención' },
];

function Estrellas({ valor, onChange, etiqueta }) {
  return (
    <div className="flex items-center gap-1" role="radiogroup" aria-label={etiqueta}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          role="radio"
          aria-checked={valor === n}
          aria-label={`${n} de 5`}
          onClick={() => onChange(n)}
          className="p-1 -m-0.5"
        >
          <Star className={`w-7 h-7 ${n <= valor ? 'fill-amber-400 text-amber-400' : 'text-gray-300 dark:text-gray-600'}`} />
        </button>
      ))}
    </div>
  );
}

const BTN = 'px-8 py-3 font-semibold disabled:opacity-50 w-full sm:w-auto';
const BTN_NEGRO = `${BTN} bg-black dark:bg-white text-white dark:text-black`;
const BTN_LINEA = `${BTN} border border-gray-300 dark:border-gray-700`;

export default function ResponderTicket() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const [ticket, setTicket] = useState(null);
  const [error, setError] = useState('');
  const [modo, setModo] = useState(null); // null | 'solucionado' | 'no_solucionado'
  const [mensaje, setMensaje] = useState('');
  const [notas, setNotas] = useState({ calificacion: 0, calificacionRapidez: 0, calificacionAtencion: 0 });
  const [enviando, setEnviando] = useState(false);
  const [enviado, setEnviado] = useState(null); // tipo enviado

  useEffect(() => {
    http.get(`/tickets/responder/${token}`)
      .then((r) => {
        setTicket(r.data);
        // Enlaces del correo: ?cal=N (estrellas con un clic) o ?accion=no
        if (r.data.pendienteConfirmar && !r.data.cerrado) {
          const cal = Number(params.get('cal'));
          if (cal >= 1 && cal <= 5) {
            setNotas((p) => ({ ...p, calificacion: cal }));
            setModo('solucionado');
          } else if (params.get('accion') === 'no') {
            setModo('no_solucionado');
          }
        }
      })
      .catch((e) => setError(e.response?.data?.message || 'No se pudo abrir el enlace.'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const enviar = async (tipo) => {
    setEnviando(true);
    setError('');
    try {
      await http.post(`/tickets/responder/${token}`, {
        tipo,
        mensaje,
        ...(tipo === 'solucionado' ? notas : {}),
      });
      setEnviado(tipo);
    } catch (err) {
      setError(err.response?.data?.message || 'No se pudo enviar tu respuesta. Intenta nuevamente.');
    } finally {
      setEnviando(false);
    }
  };

  const submit = (tipo) => (e) => {
    e.preventDefault();
    enviar(tipo);
  };

  const titulo = 'text-3xl font-extrabold uppercase tracking-tight leading-tight';

  return (
    <div className="min-h-screen bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100">
      <div className="max-w-xl mx-auto px-6 py-10">
        <div className="flex items-center gap-2.5 mb-10">
          <span className="w-7 h-7 rounded-md bg-black dark:bg-white text-white dark:text-black flex items-center justify-center text-sm font-extrabold">H</span>
          <span className="font-bold text-lg">HelpDesk</span>
        </div>

        {!ticket && !error && <p className="text-sm text-gray-500">Cargando…</p>}

        {error && !ticket && (
          <>
            <h1 className={`${titulo} mb-4`}>Enlace no disponible</h1>
            <p className="text-gray-600 dark:text-gray-400 mb-6">{error}</p>
            <a href="/" className={`inline-block ${BTN_NEGRO}`}>Ir al portal</a>
          </>
        )}

        {ticket && enviado && (
          <>
            <h1 className={`${titulo} mb-4`}>{enviado === 'solucionado' ? '¡Gracias!' : 'Respuesta enviada'}</h1>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {enviado === 'solucionado'
                ? `Registramos tu confirmación y tu calificación. El ticket #${ticket.id} quedó cerrado.`
                : enviado === 'no_solucionado'
                  ? `Soporte recibió tu mensaje en el ticket #${ticket.id} y volverá a revisarlo.`
                  : `Soporte recibió tu respuesta en el ticket #${ticket.id} y continuará con tu solicitud.`}
            </p>
            <a href={`/tickets/${ticket.id}`} className={`inline-block ${BTN_NEGRO}`}>Ver el ticket en el portal</a>
          </>
        )}

        {ticket && !enviado && (
          <>
            <h1 className={`${titulo} mb-2`}>
              {ticket.pendienteConfirmar ? `¿Quedó solucionado? Ticket #${ticket.id}` : `Responder ticket #${ticket.id}`}
            </h1>
            <p className="text-gray-500 dark:text-gray-400 mb-8">{ticket.asunto}</p>

            {ticket.comentarios.length > 0 && (
              <div className="mb-8 space-y-3">
                {ticket.comentarios.map((c, i) => (
                  <div key={i} className="border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900 p-4">
                    <div className="text-xs text-gray-500 mb-1"><strong className="text-gray-700 dark:text-gray-300">{c.autor}</strong> · {formatear(c.fecha)}</div>
                    <p className="text-sm whitespace-pre-wrap break-words">{c.contenido}</p>
                  </div>
                ))}
              </div>
            )}

            {ticket.cerrado && (
              <p className="text-gray-600 dark:text-gray-400">Este ticket ya está cerrado. Si necesitas ayuda, crea una nueva solicitud en el portal.</p>
            )}

            {!ticket.cerrado && ticket.pendienteConfirmar && !modo && (
              <div className="flex flex-col sm:flex-row gap-3">
                <button type="button" onClick={() => setModo('solucionado')} className={BTN_NEGRO}>Sí, quedó solucionado</button>
                <button type="button" onClick={() => setModo('no_solucionado')} className={BTN_LINEA}>No está solucionado</button>
              </div>
            )}

            {!ticket.cerrado && modo === 'solucionado' && (
              <form onSubmit={submit('solucionado')} className="space-y-6">
                <p className="text-gray-600 dark:text-gray-400">Califica la atención recibida. Al enviar, el ticket se cerrará.</p>
                {CRITERIOS.map((c) => (
                  <div key={c.id}>
                    <div className="text-sm font-semibold mb-1">{c.label}{c.requerida ? '' : ' (opcional)'}</div>
                    <Estrellas valor={notas[c.id]} etiqueta={c.label} onChange={(n) => setNotas((p) => ({ ...p, [c.id]: n }))} />
                  </div>
                ))}
                <div>
                  <label className="block text-sm font-semibold mb-1" htmlFor="comentario">Comentario (opcional)</label>
                  <textarea
                    id="comentario"
                    value={mensaje}
                    onChange={(e) => setMensaje(e.target.value)}
                    rows={3}
                    maxLength={2000}
                    className="w-full border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 p-3 text-base focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white"
                  />
                </div>
                {error && <p className="text-sm text-red-600">{error}</p>}
                <div className="flex flex-col sm:flex-row gap-3">
                  <button type="submit" disabled={enviando || !notas.calificacion} className={BTN_NEGRO}>{enviando ? 'Enviando…' : 'Confirmar y calificar'}</button>
                  <button type="button" onClick={() => { setModo(null); setError(''); }} className={BTN_LINEA}>Volver</button>
                </div>
              </form>
            )}

            {!ticket.cerrado && modo === 'no_solucionado' && (
              <form onSubmit={submit('no_solucionado')} className="space-y-4">
                <label className="block text-sm font-semibold" htmlFor="motivo">¿Por qué no quedó solucionado?</label>
                <textarea
                  id="motivo"
                  value={mensaje}
                  onChange={(e) => setMensaje(e.target.value)}
                  rows={6}
                  maxLength={5000}
                  required
                  autoFocus
                  className="w-full border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 p-3 text-base focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white"
                  placeholder="Cuéntanos qué sigue fallando o qué falta"
                />
                {error && <p className="text-sm text-red-600">{error}</p>}
                <div className="flex flex-col sm:flex-row gap-3">
                  <button type="submit" disabled={enviando || mensaje.trim().length < 5} className={BTN_NEGRO}>{enviando ? 'Enviando…' : 'Enviar a soporte'}</button>
                  <button type="button" onClick={() => { setModo(null); setError(''); }} className={BTN_LINEA}>Volver</button>
                </div>
              </form>
            )}

            {!ticket.cerrado && !ticket.pendienteConfirmar && (
              <form onSubmit={submit('mensaje')} className="space-y-4">
                <label className="block text-sm font-semibold" htmlFor="respuesta">Tu respuesta</label>
                <textarea
                  id="respuesta"
                  value={mensaje}
                  onChange={(e) => setMensaje(e.target.value)}
                  rows={6}
                  maxLength={5000}
                  required
                  className="w-full border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 p-3 text-base focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white"
                  placeholder="Escribe aquí tu respuesta para soporte"
                />
                {error && <p className="text-sm text-red-600">{error}</p>}
                <button type="submit" disabled={enviando || mensaje.trim().length < 2} className={BTN_NEGRO}>{enviando ? 'Enviando…' : 'Enviar respuesta'}</button>
              </form>
            )}
          </>
        )}
      </div>
    </div>
  );
}
